import React, { useMemo } from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useMutation, useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";
import { FETCH_PROJECT_QUERY } from "../../../constants/graphql/queries";
import { GET_SHIPMENT_QUERY } from "../../../constants/graphql/queries";
import { DELETE_SHIPMENT_MUTATION } from "../../../constants/graphql/mutations";
import DeleteShipmentModal from "../../project/inbound/DeleteShipmentModal";
import LeaveReceivingAssistantModal from "./LeaveReceivingAssistantModal";
import useQueryParams from "../../../hooks/useQueryParams";
import useWizardStep from "../../../hooks/useWizardStep";

const BASE_STEPS = ["info", "details", "shipment", "items", "additional-charges", "os&d", "confirmation"];
const STEPS_WITH_RESERVATIONS = [...BASE_STEPS.slice(0, 5), "reservations", ...BASE_STEPS.slice(5)];
import { getShipmentValues } from "../../../utils/shipment";
import { fullWidthDialog } from "../../../constants/dialog";
import Shipment from "./Shipment/Shipment";
import Items from "./Items/Items";
import Details from "./Details/Details";
import OSAndD from "./OSAndD/OSAndD";
import AdditionalCharges from "./AdditionalCharges/AdditionalCharges";
import Reservations from "./Reservations/Reservations";
import Confirmation from "./Confirmation/Confirmation";
import useUnsavedChanges from "src/hooks/useUnsavedChanges";
import usePdfSignedUrl from "src/hooks/usePdfSignedUrl";
import UnsavedChangesDialog from "src/components/modals/UnsavedChanges/UnsavedChangesDialog";
import VerticalReceivingWizard from "../../../views/review/VerticalReceivingWizard";
import ScannedCompletedShipmentInfoStep from "./Details/ScannedCompletedShipmentInfoStep";
import useReceivingWizardFlow from "./hooks/useReceivingWizardFlow";
import { clearReservationPlan } from "./Reservations/storage";

export default function ReceivingWizardModal({
  type,
  onToggle,
  projectId: propsProjectId,
  dialogComponent,
  verticalLayout = false,
}) {
  // Use vertical layout when dialogComponent is provided (overview context)
  const useVerticalLayout = verticalLayout || !!dialogComponent;
  const [openRemoveShipment, setOpenRemoveShipment] = React.useState(false);
  const [openLeaveWizardModal, setOpenLeaveWizardModal] = React.useState(false);
  const params = useQueryParams();
  const id = params.get("id");
  const editShipment = params.get("editShipment") === "true";
  const { projectId: routeProjectId } = useParams();
  const queryProjectId = params.get("receivingProjectId") || params.get("projectId");
  const projectId = propsProjectId || routeProjectId || queryProjectId;
  const shipmentFormikRef = React.useRef(null);
  const additionalChargesFormikRef = React.useRef(null);
  const shipmentActionsRef = React.useRef(null);
  const additionalChargesActionsRef = React.useRef(null);
  const { data: { project: fetchedProject = {} } = {}, loading: loadingProject } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });
  const [removeShipment] = useMutation(DELETE_SHIPMENT_MUTATION);
  const { data: { shipment = {} } = {}, loading: loadingShipment } = useQuery(GET_SHIPMENT_QUERY, {
    variables: { id },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    skip: !id,
  });
  const project = fetchedProject.id ? fetchedProject : shipment.project || {};
  const { hasInboundReservations } = useReceivingWizardFlow(project);
  const steps = hasInboundReservations ? STEPS_WITH_RESERVATIONS : BASE_STEPS;
  const step = useWizardStep(steps, {
    fallback: "details",
    skip: useVerticalLayout || loadingProject || loadingShipment,
  });
  const initialValues = useMemo(() => {
    const data = getShipmentValues(shipment);
    const costCenters = project.costCenters || [];
    if (shipment?.id && !shipment.costCenter?.id && costCenters.length === 1) {
      data.costCenterId = parseInt(costCenters[0].id, 10);
    }
    return data;
  }, [project?.id, shipment]);

  const {
    attemptClose,
    confirmDiscard,
    cancelDiscard,
    confirmSave,
    showDialog,
    saving,
  } = useUnsavedChanges({
    onClose: onToggle,
    isDirtyFn: () => {
      if (!editShipment) return false;
      if (step === "shipment") {
        return !!shipmentFormikRef.current?.dirty;
      }
      if (step === "additional-charges") {
        return !!additionalChargesFormikRef.current?.dirty;
      }
      return false;
    },
    onSave: async () => {
      if (step === "shipment" && shipmentActionsRef.current?.saveProgress) {
        const didSave = await shipmentActionsRef.current.saveProgress();
        if (!didSave) {
          throw new Error("Failed to save shipment progress");
        }
      } else if (step === "additional-charges" && additionalChargesActionsRef.current?.saveProgress) {
        const didSave = await additionalChargesActionsRef.current.saveProgress();
        if (!didSave) {
          throw new Error("Failed to save shipment charges");
        }
      }
    },
  });

  const closeWizard = () => {
    if (step === "confirmation" && shipment?.id) {
      clearReservationPlan(shipment.id);
    }
    onToggle();
  };

  const items = shipment?.shipmentItems || [];
  const hasAnyPositiveQuantity = items.some(v => v?.quantity > 0);
  const canDeleteShipmentOnExit = id && (items.length === 0 || !hasAnyPositiveQuantity);

  const handleClose = (_event, _reason) => {
    const isOnsiteInventory = shipment?.shipmentStatus === "onsite_inventory";

    if (editShipment) {
      if (["details", "confirmation"].includes(step)) {
        closeWizard();
      } else {
        attemptClose();
      }
      return;
    }

    if (isOnsiteInventory) {
      closeWizard();
      return;
    }

    if (!id || step === "confirmation") {
      closeWizard();
      return;
    }

    setOpenLeaveWizardModal(true);
  };

  const handleRemoveShipment = (deleteCorrespondingAssets = false) => {
    return removeShipment({
      variables: { id, deleteCorrespondingAssets },
    }).then(() => {
      closeWizard();
    });
  };

  const handleSaveProgress = async () => {
    setOpenLeaveWizardModal(false);

    if (step === "shipment" && shipmentActionsRef.current?.saveProgress) {
      const didSave = await shipmentActionsRef.current.saveProgress();
      if (didSave) {
        closeWizard();
      }
      return;
    }

    if (step === "additional-charges" && additionalChargesActionsRef.current?.saveProgress) {
      const didSave = await additionalChargesActionsRef.current.saveProgress();
      if (didSave) {
        closeWizard();
      }
      return;
    }

    closeWizard();
  };

  const DialogComponent = dialogComponent || Dialog;
  const { pdfUrl } = usePdfSignedUrl("shipment_pdfs", id, {
    enabled: !!id,
  });

  return (
    <DialogComponent
      open
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={fullWidthDialog}
      pdfUrl={pdfUrl}
      shipment={shipment}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {loadingProject || loadingShipment ? (
          <Box p={3} display="flex" minHeight={400} alignItems="center" justifyContent="center">
            <CircularProgress color="primary" />
          </Box>
        ) : useVerticalLayout ? (
          // Vertical layout for overview/approval context
          (<VerticalReceivingWizard
            shipment={shipment}
            project={project}
            onClose={handleClose}
            type={type}
          />)
        ) : (
          // Original step-based layout
          (<Box minHeight={400} height="100%" flexGrow={1} display="flex" flexDirection="column">
            {step === "info" && (
              <ScannedCompletedShipmentInfoStep
                shipment={shipment}
                project={project}
              />
            )}
            {step === "details" && <Details project={project} onClose={handleClose} />}
            {step === "shipment" && (
              <Shipment
                shipment={shipment}
                project={project}
                initialValues={initialValues}
                onClose={handleClose}
                formikRef={shipmentFormikRef}
                actionsRef={shipmentActionsRef}
              />
            )}
            {step === "items" && (
              <Items shipment={shipment} project={project} onClose={handleClose} />
            )}
            {step === "additional-charges" && (
              <AdditionalCharges
                project={project}
                shipment={shipment}
                onClose={handleClose}
                formikRef={additionalChargesFormikRef}
                actionsRef={additionalChargesActionsRef}
              />
            )}
            {step === "reservations" && hasInboundReservations && (
              <Reservations shipment={shipment} project={project} onClose={handleClose} />
            )}
            {step === "os&d" && (
              <OSAndD type={type} shipment={shipment} project={project} onClose={handleClose} />
            )}
            {step === "confirmation" && (
              <Confirmation shipment={shipment} project={project} onClose={handleClose} />
            )}
          </Box>)
        )}
      </Box>
      {openRemoveShipment && (
        <DeleteShipmentModal
          title="Delete Shipment"
          descr="Are you sure want to delete this shipment?"
          onClose={() => setOpenRemoveShipment(false)}
          onDelete={handleRemoveShipment}
          shipment={shipment}
        />
      )}
      {openLeaveWizardModal && (
        <LeaveReceivingAssistantModal
          canDelete={canDeleteShipmentOnExit}
          onClose={() => setOpenLeaveWizardModal(false)}
          onContinue={() => setOpenLeaveWizardModal(false)}
          onSaveProgress={handleSaveProgress}
          onDelete={() => {
            setOpenLeaveWizardModal(false);
            setOpenRemoveShipment(true);
          }}
        />
      )}
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={saving}
        />
      )}
    </DialogComponent>
  );
}
